import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserRound, KeyRound, Trash2, CalendarCheck, Route, GaugeCircle, Flag, Briefcase, Send, Camera, IdCard, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { resizeImage } from "../../lib/img";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { fmtDate, fmtDateTime } from "../../lib/format";
import { ROLE, ROLE_DESC } from "./UsersSettings";

const BOOKING_STATUS = {
  menunggu: "bg-slate-500 hover:bg-slate-500",
  diluluskan: "bg-[#198754] hover:bg-[#198754]",
  ditolak: "bg-[#DC3545] hover:bg-[#DC3545]",
  selesai: "bg-[#10056E] hover:bg-[#10056E]",
};

export default function UserDetail({ id }) {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [units, setUnits] = useState([]);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);
  const licenseRef = useRef(null);
  const [expiry, setExpiry] = useState("");

  const uploadMedia = async (patch) => {
    try {
      await api.put(`/users/${id}/media`, patch);
      toast.success("Media pengguna dikemas kini");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMedia({ photo: await resizeImage(file, 400), photo_thumb: await resizeImage(file, 96, 0.72) });
    e.target.value = "";
  };

  const onLicenseFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMedia({ license_image: await resizeImage(file, 900) });
    e.target.value = "";
  };

  const load = () => api.get(`/users/${id}`).then((r) => {
    setData(r.data);
    const u = r.data.user;
    setForm({ name: u.name, email: u.email, phone: u.phone || "", role: u.role, unit_id: u.unit_id || "", staff_id: u.staff_id || "", nric: u.nric || "" });
    setExpiry(u.license_expiry || "");
  }).catch((err) => {
    toast.error(apiError(err));
    navigate("/tetapan/pengguna");
  });
  useEffect(() => { load(); api.get("/units").then((r) => setUnits(r.data)); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${id}`, { ...form, ...(password ? { password } : {}) });
      toast.success("Maklumat pengguna dikemas kini");
      setPassword("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Padam pengguna ${data.user.name}?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna dipadam");
      navigate("/tetapan/pengguna");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resendActivation = async () => {
    try {
      await api.post(`/users/${id}/resend-activation`);
      toast.success("Emel pengaktifan dihantar semula");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!data || !form) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;
  const { user: u, stats, recent_bookings, recent_trips } = data;
  const isSelf = u.id === me?.id;

  return (
    <div className="space-y-5" data-testid="user-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate("/tetapan/pengguna")} data-testid="user-detail-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
              {u.name} <Badge className={ROLE[u.role]?.cls}>{ROLE[u.role]?.label}</Badge>
              {u.status === "belum_aktif" ? (
                <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50" data-testid="user-detail-status">Belum Aktif</Badge>
              ) : (
                <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="user-detail-status">Aktif</Badge>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">{u.email}{u.staff_id ? ` · ID Staf ${u.staff_id}` : ""} · Didaftar {fmtDate(u.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {u.status === "belum_aktif" && (
            <Button variant="outline" className="border-[#10056E] text-[#10056E] hover:bg-indigo-50" onClick={resendActivation} data-testid="user-detail-resend-btn">
              <Send className="h-4 w-4 mr-2" /> Hantar Semula Pengaktifan
            </Button>
          )}
          {!isSelf && (
            <Button variant="outline" className="text-[#DC3545] border-[#DC3545] hover:bg-red-50" onClick={remove} data-testid="user-detail-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Padam Pengguna
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: CalendarCheck, label: "Tempahan", value: stats.total_bookings, sub: `${stats.pending_bookings} menunggu` },
          { icon: Route, label: "Perjalanan Dilog", value: stats.total_trips, sub: `${stats.total_distance} km` },
          { icon: GaugeCircle, label: "Purata Km/L", value: stats.avg_kmpl ?? "-", sub: "Km/Liter" },
          { icon: Flag, label: "Bahan Api", value: `${stats.total_liters ?? 0} L`, sub: "jumlah dibeli" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <s.icon className="h-4 w-4 text-[#10056E]" />
            <p className="text-2xl font-bold mt-2">{s.value}</p>
            <p className="text-xs font-semibold">{s.label}</p>
            <p className="text-[11px] text-muted-foreground">{s.sub}</p>
          </div>
        ))}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><UserRound className="h-4 w-4" /> Maklumat Pengguna</CardTitle>
          <CardDescription>Perubahan nama & emel akan dikemas kini pada semua tempahan dan log berkaitan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" /> Foto & Dokumen
            </p>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="user-photo-input" />
            <input ref={licenseRef} type="file" accept="image/*" className="hidden" onChange={onLicenseFile} data-testid="user-license-input" />
            <div className="flex flex-wrap items-start gap-6">
              <div className="space-y-2">
                {u.photo ? (
                  <img src={u.photo} alt={u.name} className="h-20 w-20 rounded-full object-cover border-2 border-[#10056E]/20" data-testid="user-photo-img" />
                ) : (
                  <span className="h-20 w-20 rounded-full bg-slate-100 border flex items-center justify-center text-slate-400"><UserRound className="h-8 w-8" /></span>
                )}
                <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} data-testid="user-photo-btn">
                  <Camera className="h-3.5 w-3.5 mr-1.5" /> Foto Profil
                </Button>
              </div>
              {u.role === "pemandu" && (
                <div className="flex-1 min-w-64 space-y-2" data-testid="user-license-section">
                  <p className="text-xs font-semibold flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5 text-[#10056E]" /> Lesen Memandu</p>
                  {u.license_image ? (
                    <img src={u.license_image} alt="Lesen" className="max-h-36 rounded-lg border" data-testid="user-license-img" />
                  ) : (
                    <p className="text-xs text-muted-foreground border border-dashed rounded-lg p-3">Tiada gambar lesen.</p>
                  )}
                  <div className="flex flex-wrap items-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => licenseRef.current?.click()} data-testid="user-license-btn">
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> {u.license_image ? "Tukar Lesen" : "Muat Naik Lesen"}
                    </Button>
                    <div className="space-y-1">
                      <Label className="text-[11px]">Tarikh Luput</Label>
                      <div className="flex gap-2">
                        <Input type="date" className="h-9 w-40" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="user-license-expiry-input" />
                        <Button type="button" size="sm" className="h-9 bg-[#10056E] hover:bg-[#2a17a8]" onClick={() => uploadMedia({ license_expiry: expiry })} data-testid="user-license-expiry-save">Simpan</Button>
                      </div>
                    </div>
                    {expiry && new Date(expiry) < new Date() && <Badge className="bg-[#DC3545] hover:bg-[#DC3545]">Lesen Tamat Tempoh</Badge>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
              <UserRound className="h-3.5 w-3.5" /> Maklumat Peribadi
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nama Penuh <span className="text-[#DC3545]">*</span></Label>
                <Input data-testid="edit-user-name-input" value={form.name} placeholder="Cth: Ahmad bin Abdullah" onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>ID Staf</Label>
                <Input data-testid="edit-user-staffid-input" value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} placeholder="Cth: MBDK-0123" />
              </div>
              <div className="space-y-1.5">
                <Label>Emel <span className="text-[#DC3545]">*</span></Label>
                <Input data-testid="edit-user-email-input" type="email" value={form.email} placeholder="nama@agensi.gov.my" onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>No. Telefon</Label>
                <Input data-testid="edit-user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="012-3456789" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>No. Kad Pengenalan (MyKad/MyTentera/MyPDRM)</Label>
                <Input data-testid="edit-user-nric-input" value={form.nric} onChange={(e) => setForm({ ...form, nric: e.target.value })} placeholder="Cth: 900101-01-1234" />
                <p className="text-[11px] text-muted-foreground">Digunakan sebagai pengecam utama untuk log masuk MyDigital ID SSO.</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5" /> Penugasan
            </p>
            <div className="space-y-1.5">
              <Label>Peranan <span className="text-[#DC3545]">*</span></Label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="edit-user-role-options">
                {Object.entries(ROLE).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    disabled={isSelf}
                    onClick={() => setForm({ ...form, role: k })}
                    data-testid={`edit-user-role-option-${k}`}
                    className={`text-left border rounded-lg p-3 transition-colors ${
                      form.role === k
                        ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50"
                        : "border-slate-200 hover:border-[#10056E]/50"
                    } ${isSelf ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <p className="text-sm font-bold">{v.label}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{ROLE_DESC[k]}</p>
                  </button>
                ))}
              </div>
              {isSelf && <p className="text-xs text-muted-foreground">Anda tidak boleh menukar peranan sendiri.</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Bahagian / Unit</Label>
              <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                <SelectTrigger data-testid="edit-user-unit-select"><SelectValue placeholder="Pilih Bahagian/Unit" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56">
                  {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Keselamatan
            </p>
            <div className="space-y-1.5">
              <Label>Set Semula Kata Laluan (pilihan, min. 6 aksara)</Label>
              <div className="relative max-w-sm">
                <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="edit-user-password-input" type="text" className="pl-9" minLength={6} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Biarkan kosong jika tiada perubahan" />
              </div>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="edit-user-save-btn">
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-base">Tempahan Terkini</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recent_bookings.length === 0 && <p className="text-sm text-muted-foreground">Tiada tempahan.</p>}
            {recent_bookings.map((b) => (
              <button key={b.id} onClick={() => navigate(`/tempahan/${b.id}`)}
                className="w-full text-left border rounded-lg p-3 hover:border-[#10056E]/50 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold truncate">{b.destination}</p>
                  <Badge className={`${BOOKING_STATUS[b.status]} text-[10px] shrink-0`}>{b.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(b.start_datetime)} · {b.vehicle_reg || "belum ditugaskan"}</p>
              </button>
            ))}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-base">Log Perjalanan Terkini</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recent_trips.length === 0 && <p className="text-sm text-muted-foreground">Tiada log perjalanan.</p>}
            {recent_trips.map((t) => (
              <button key={t.id} onClick={() => navigate(`/log-perjalanan/${t.id}`)}
                className="w-full text-left border rounded-lg p-3 hover:border-[#10056E]/50 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{t.vehicle_reg || t.vehicle_id} · {t.distance_km} km</p>
                  <Badge className="bg-[#10056E] hover:bg-[#10056E] text-[10px] shrink-0">{t.kmpl ?? "-"} Km/L</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(t.start_datetime)}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
